import React, { createContext, useState, useCallback, useContext, useEffect } from 'react';
import { isAfter } from 'date-fns';

import api, { AUTH_LOGOUT_EVENT } from './../services/api';
import { Props } from '../global/types';
import { setupOwnerSubLoginApiGuards } from '../services/setupOwnerSubLoginApiGuards';
import { pushLogin, pushSignUp } from '../utils/dataLayer';

const SESSION_EXPIRATION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

interface ISignInCredentials {
	email: string;
	password: string;
}

interface IRegisterUserCredentials {
	first_name: string | undefined;
	email: string | undefined;
	phone: string;
	type: string;
	password: string | undefined;
	password_confirmation: string | undefined;
}

interface IUpdateUserCredentials {
	first_name: string;
	email: string;
	phone: string;
	password: string;
	password_confirmation: string;
}

interface IUpdateUserCredentialsResponse {
	message: string;
}

export interface IScheduleCredentials {
	id: number;
	video_call?: string;
	property_id: string;
	userChannel?: string;
	origin_source?: string | null;
	utm_source?: string | null;
	utm_campaign?: string | null;
	utm_medium?: string | null;
	utm_content?: string | null;
}

interface IScheduleCredentialsResponse {
	message: string;
	success: boolean;
}

interface IUser {
	id: number;
	first_name: string;
	email: string;
	phone: string;
	type: string;
	role?: string;
	is_admin?: boolean;
	isAdmin?: boolean;
	/** True when authenticated as an alternate owner email (sub-login). */
	is_sub_login?: boolean;
	/** Master owner user id when is_sub_login is true. */
	master_user_id?: number | null;
}

interface IAuthState {
	user: IUser;
	access_token: string;
	dateToExpires: Date;
}

interface ISignInCredentialsReturn {
	message?: string;
}

interface ISignInGoogleCredentials {
	code: string;
}

interface ISignInPhoneCredentials {
	phone: string;
}

interface IVerifyPhoneOtpCredentials {
	phone: string;
	otp: string;
}

interface ICompletePhoneRegistrationCredentials {
	phone: string;
	otp: string;
	first_name: string;
	type: 'tenant' | 'owner';
}

interface IPhoneAuthResponse {
	requiresPhoneCompletion?: boolean;
	message?: string;
	phone?: string;
	access_token?: string;
	user?: IUser;
	expires_in?: number;
}

interface IContext {
	signIn(credentials: ISignInCredentials): Promise<void>;
	signInFacebook(credentials: ISignInCredentials): Promise<ISignInCredentialsReturn | void>;
	signInGoogle(credentials: ISignInGoogleCredentials): Promise<IPhoneAuthResponse | void>;
	sendPhoneOtp(credentials: ISignInPhoneCredentials): Promise<void>;
	verifyPhoneOtp(credentials: IVerifyPhoneOtpCredentials): Promise<IPhoneAuthResponse | void>;
	completePhoneRegistration(credentials: ICompletePhoneRegistrationCredentials): Promise<void>;
	registerUser(credentials: IRegisterUserCredentials): Promise<void>;
	logOut(): Promise<void>;
	updateUser(credentials: IUpdateUserCredentials): Promise<IUpdateUserCredentialsResponse>;
	scheduleAppointment(credentials: IScheduleCredentials): Promise<IScheduleCredentialsResponse>;
	user: IUser | undefined;
	access_token: string;
	dateToExpires: Date;
}

const AuthContext = createContext<IContext>({} as IContext);

const AuthProvider: React.FC<Props> = ({ children }) => {
	const [data, setData] = useState<IAuthState>(() => {
		try {
			const access_token = localStorage.getItem('@7Cantos:token');
			const user = localStorage.getItem('@7Cantos:user');
			const dateToExpires = localStorage.getItem('@7Cantos:dateToExpires');

			if (!access_token || !user || !dateToExpires) {
				return {} as IAuthState;
			}

			if (user === 'undefined' || user === 'null') {
				localStorage.removeItem('@7Cantos:token');
				localStorage.removeItem('@7Cantos:user');
				localStorage.removeItem('@7Cantos:dateToExpires');
				return {} as IAuthState;
			}

			const parsedUser = JSON.parse(user);

			if (!parsedUser || typeof parsedUser !== 'object') {
				localStorage.removeItem('@7Cantos:token');
				localStorage.removeItem('@7Cantos:user');
				localStorage.removeItem('@7Cantos:dateToExpires');
				return {} as IAuthState;
			}

			api.defaults.headers.authorization = `Bearer ${access_token}`;

			return { user: parsedUser, access_token, dateToExpires: new Date(dateToExpires) };
		} catch (error) {
			localStorage.removeItem('@7Cantos:token');
			localStorage.removeItem('@7Cantos:user');
			localStorage.removeItem('@7Cantos:dateToExpires');
			return {} as IAuthState;
		}
	});

	const signIn = useCallback(async ({ email, password }: ISignInCredentials) => {
		const responseToken = await api.post('/api/auth/login', {
			email,
			password
		});

		const { access_token } = responseToken.data;

		const expiresIn = responseToken.data.expires_in;

		const actualDate = new Date(Date.now());

		const dateToExpires = new Date(actualDate.getFullYear(), actualDate.getMonth(), actualDate.getDate(), actualDate.getHours(), actualDate.getMinutes(), actualDate.getSeconds() + expiresIn);

		api.defaults.headers.authorization = `Bearer ${access_token}`;

		const responseUser = await api.post('/api/auth/me', {
			headers: {
				Authorization: `Bearer ${access_token}`
			}
		});

		const user = responseUser.data;

		localStorage.setItem('@7Cantos:token', access_token);
		localStorage.setItem('@7Cantos:user', JSON.stringify(user));
		localStorage.setItem('@7Cantos:dateToExpires', dateToExpires.toString());

		setData({ user, access_token, dateToExpires });
		pushLogin('email', user);
	}, []);

	const signInFacebook = useCallback(async ({ email, password }: ISignInCredentials): Promise<ISignInCredentialsReturn | void> => {
		const response = await api.post('/api/auth/login/facebook', {
			email,
			password
		});

		if (!!response.data.success) {
			return response.data.message;
		}

		const { access_token } = response.data;

		const expiresIn = response.data.expires_in;

		const actualDate = new Date(Date.now());

		const dateToExpires = new Date(actualDate.getFullYear(), actualDate.getMonth(), actualDate.getDate(), actualDate.getHours(), actualDate.getMinutes(), actualDate.getSeconds() + expiresIn);

		api.defaults.headers.authorization = `Bearer ${access_token}`;

		const responseUser = await api.post('/api/auth/me', {
			headers: {
				Authorization: `Bearer ${access_token}`
			}
		});

		const user = responseUser.data;

		localStorage.setItem('@7Cantos:token', access_token);
		localStorage.setItem('@7Cantos:user', JSON.stringify(user));
		localStorage.setItem('@7Cantos:dateToExpires', dateToExpires.toString());

		setData({ user, access_token, dateToExpires });
		pushLogin('facebook', user);
	}, []);

	const logOut = useCallback(async () => {
		const access_token = localStorage.getItem('@7Cantos:token');

		if (access_token) {
			api.defaults.headers.authorization = `Bearer ${access_token}`;

			try {
				await api.post('/api/auth/logout');
			} catch (error) { }
		}
		localStorage.removeItem('@7Cantos:token');
		localStorage.removeItem('@7Cantos:user');
		localStorage.removeItem('@7Cantos:dateToExpires');
		setData({} as IAuthState);
	}, []);

	useEffect(() => {
		return setupOwnerSubLoginApiGuards({
			onUnauthorizedSubLogin: () => {
				void logOut();
				window.location.assign('/login?reason=deactivated');
			},
			onForbiddenSubLogin: () => {
				if (window.location.pathname === '/owner-dashboard') {
					return;
				}
				window.location.assign('/owner-dashboard');
			},
		});
	}, [logOut]);

	const registerUser = useCallback(async ({ first_name, email, phone, type, password, password_confirmation }: IRegisterUserCredentials) => {
		const responseRegister = await api.post('/api/auth/register', {
			first_name,
			email,
			phone,
			type,
			password,
			password_confirmation
		});

		const { access_token } = responseRegister.data;

		const expiresIn = responseRegister.data.expires_in;

		const actualDate = new Date(Date.now());

		const dateToExpires = new Date(actualDate.getFullYear(), actualDate.getMonth(), actualDate.getDate(), actualDate.getHours(), actualDate.getMinutes(), actualDate.getSeconds() + expiresIn);

		api.defaults.headers.authorization = `Bearer ${access_token}`;

		const responseUser = await api.post('/api/auth/me', {
			headers: {
				Authorization: `Bearer ${access_token}`
			}
		});

		const user = responseUser.data;

		localStorage.setItem('@7Cantos:token', access_token);
		localStorage.setItem('@7Cantos:user', JSON.stringify(user));
		localStorage.setItem('@7Cantos:dateToExpires', dateToExpires.toString());

		setData({ user, access_token, dateToExpires });
		pushSignUp('email', user);
	}, []);

	const updateUser = useCallback(async ({ first_name, email, phone, password, password_confirmation }: IUpdateUserCredentials) => {
		const access_token = localStorage.getItem('@7Cantos:token');
		const dateToExpires = localStorage.getItem('@7Cantos:dateToExpires');

		api.defaults.headers.authorization = `Bearer ${access_token}`;

		const responseUpdate = await api.put('/api/client/users', {
			first_name,
			email,
			phone,
			password,
			password_confirmation
		});

		const responseUser = await api.post('/api/auth/me', {
			headers: {
				Authorization: `Bearer ${access_token}`
			}
		});

		const user = responseUser.data;

		localStorage.setItem('@7Cantos:user', JSON.stringify(user));

		if (!!access_token && dateToExpires) {
			setData({ user, access_token, dateToExpires: new Date(dateToExpires) });
		}

		return {
			message: responseUpdate.data.message
		};
	}, []);

	const scheduleAppointment = useCallback(async (data: IScheduleCredentials) => {
		const access_token = localStorage.getItem('@7Cantos:token');

		api.defaults.headers.authorization = `Bearer ${access_token}`;

		const response = await api.put(
			`/api/client/schedules/${data.id}`,
			data
		);

		localStorage.removeItem('@7Cantos:Campaign');

		return {
			message: response.data.message,
			success: response.data.success,
		};
	}, []);


	const saveUserSession = useCallback((user: IUser, access_token: string, expiresIn: number) => {
		const actualDate = new Date(Date.now());
		const dateToExpires = new Date(actualDate.getFullYear(), actualDate.getMonth(), actualDate.getDate(), actualDate.getHours(), actualDate.getMinutes(), actualDate.getSeconds() + expiresIn);

		api.defaults.headers.authorization = `Bearer ${access_token}`;

		localStorage.setItem('@7Cantos:token', access_token);
		localStorage.setItem('@7Cantos:user', JSON.stringify(user));
		localStorage.setItem('@7Cantos:dateToExpires', dateToExpires.toString());

		setData({ user, access_token, dateToExpires });
	}, []);

	const signInGoogle = useCallback(async ({ code }: ISignInGoogleCredentials): Promise<IPhoneAuthResponse | void> => {
		try {
			const response = await api.post('/api/auth/login/google', {
				code
			});

			if (response.data.requiresPhoneCompletion) {
				return {
					requiresPhoneCompletion: true,
					message: response.data.message,
					phone: response.data.phone
				};
			}

			const { access_token, expires_in, user } = response.data;

			saveUserSession(user, access_token, expires_in);
			pushLogin('google', user);
		} catch (error: any) {
			throw error;
		}
	}, [saveUserSession]);

	const sendPhoneOtp = useCallback(async ({ phone }: ISignInPhoneCredentials) => {
		const response = await api.post('/api/auth/phone/request-otp', {
			phone
		});

		localStorage.setItem('@7Cantos:tempPhone', phone);

		return response.data;
	}, []);

	const verifyPhoneOtp = useCallback(async ({ phone, otp }: IVerifyPhoneOtpCredentials): Promise<IPhoneAuthResponse | void> => {
		try {
			const response = await api.post('/api/auth/phone/verify-otp', {
				phone,
				code: otp
			});

			if (response.data.requiresPhoneCompletion) {
				return {
					requiresPhoneCompletion: true,
					message: response.data.message,
					phone: phone
				};
			}

			const { access_token, expires_in } = response.data;

			if (!access_token) {
				throw new Error('Token não retornado pelo servidor');
			}

			const userResponse = await api.post('/api/auth/me', {}, {
				headers: {
					Authorization: `Bearer ${access_token}`
				}
			});

			const user = userResponse.data;

			if (!user) {
				throw new Error('Dados do usuário não obtidos');
			}

			saveUserSession(user, access_token, expires_in);
			pushLogin('phone', user);
			localStorage.removeItem('@7Cantos:tempPhone');

			return { access_token, user, expires_in };
		} catch (error: any) {
			throw error;
		}
	}, [saveUserSession]);

	const completePhoneRegistration = useCallback(async ({ phone, otp, first_name, type }: ICompletePhoneRegistrationCredentials) => {
		const response = await api.post('/api/auth/complete-phone-registration', {
			phone,
			otp,
			first_name,
			type
		});

		const { access_token, expires_in, user } = response.data;

		saveUserSession(user, access_token, expires_in);
		pushSignUp('phone', user);
		localStorage.removeItem('@7Cantos:tempPhone');
	}, [saveUserSession]);

	const clearLocalSession = useCallback(() => {
		localStorage.removeItem('@7Cantos:token');
		localStorage.removeItem('@7Cantos:user');
		localStorage.removeItem('@7Cantos:dateToExpires');
		delete api.defaults.headers.authorization;
		setData({} as IAuthState);
	}, []);

	useEffect(() => {
		const handleForcedLogout = () => clearLocalSession();

		window.addEventListener(AUTH_LOGOUT_EVENT, handleForcedLogout);

		return () => {
			window.removeEventListener(AUTH_LOGOUT_EVENT, handleForcedLogout);
		};
	}, [clearLocalSession]);

	useEffect(() => {
		const checkSessionExpiration = () => {
			const storedExpiration = localStorage.getItem('@7Cantos:dateToExpires');

			if (!storedExpiration) {
				return;
			}

			if (!isAfter(new Date(Date.now()), new Date(storedExpiration))) {
				return;
			}

			clearLocalSession();
		};

		checkSessionExpiration();
		const interval = setInterval(
			checkSessionExpiration,
			SESSION_EXPIRATION_CHECK_INTERVAL_MS
		);

		return () => clearInterval(interval);
	}, [clearLocalSession]);

	return (
		<AuthContext.Provider value={{
			signIn,
			signInFacebook,
			signInGoogle,
			sendPhoneOtp,
			verifyPhoneOtp,
			completePhoneRegistration,
			registerUser,
			logOut,
			updateUser,
			scheduleAppointment,
			user: data.user,
			access_token: data.access_token,
			dateToExpires: data.dateToExpires
		}}>
			{children}
		</AuthContext.Provider>
	);
};

function useAuth(): IContext {
	const context = useContext(AuthContext);

	return context;
}

export { AuthProvider, useAuth };